import {
  CancelOutlined,
  ChatOutlined,
  CheckCircleOutline,
} from '@mui/icons-material';
import StarIcon from '@mui/icons-material/Star';
import { Box, MenuItem, Stack as MuiStack, Stack } from '@mui/material';
import { ReactNode, useCallback, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { format, parseISO } from 'date-fns';
import Popover from '@mui/material/Popover';

import { TimesheetItem } from '../TimesheetList';
import {
  TimeSheetStatus,
  calculateNetTime,
  getTimeSheetStatus,
  timesheetFlagMessage,
} from '../util';

import ProfileModalRevamp from './ProfileModal/ProfileModalRevamp';

import {
  CancelNoShowIcon,
  GravyTrained,
  MapMarkerIcon,
  InfoItalicIcon,
  AddTipIcon,
  RemoveIcon,
  AccountDollarIcon,
  NoshowIcon,
  HeartFillIcon,
  HeartIcon,
  BlockIconWithBackground,
} from '@/assets/icons';
import ClientApproveIcon from '@/assets/icons/ClientApprove';
import ClientUnRejectIcon from '@/assets/icons/ClientUnReject';
import Avatar from '@/components/Avatar';
import StyledChip from '@/components/Chip';
import { Body } from '@/components/Typography';
import Text from '@/elements/Text';
import styled from '@/styles';
import {
  GetJobQuery,
  JobStatusEnum,
  JobWorkerStatusEnum,
  RelationshipKindEnum,
  TimesheetStatusEnum,
} from '@/types/graphql';
import { centsToCurrency } from '@/util/number';
import { containsGravyCertification } from '@/util/worker';
import IconicButton from '@/components/IconicButton';
import { faCircle } from '@fortawesome/free-solid-svg-icons';
import PermissionComponent from '@/routes/PermissionComponent';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { FEATURE_TOGGLE } from '@/constants/featuretoggle';
import colors from '@/styles/colors';
import CustomTooltipJob from './TooltipJob';
import useAuth from '@/hooks/useAuth';
import { NoWrapSpan } from './style';

const IconColor = '#1C1C1C';
interface WorkerPayRateProps {
  job: GetJobQuery['job'];
  workerId: any;
  timesheet?: TimesheetItem;
  clientAdmin: boolean;
  addSeperator?: boolean;
}

const CardCriteria = styled('span', {
  fontSize: '12px',
  fontWeight: '600',
  color: '#262626',
});

const ApprovalAlert = styled('p', {
  fontSize: '12px',
  fontWeight: '400',
  color: '#fff',
  background: '#ECA300',
  padding: '3px 8px',
  borderRadius: '8px',
  textAlign: 'center',
  whiteSpace: 'nowrap',
});

/**
 * GRAVY-10453 — which OCCURRENCE of a multi-shift job the job page is showing.
 *
 * `job.timesheets` holds one row per (job_worker, shift), so matching a worker
 * alone returns whichever occurrence the API happened to list first. That was
 * invisible while the page only ever rendered one occurrence, and it is what
 * made the Worker Profile's "Review shift" link land on the right job showing
 * the wrong day.
 *
 * Both helpers take `focusedShiftId` as nullable and fall back to the previous
 * behaviour when it is absent, so the page is unchanged for every caller that
 * does not name a shift.
 */
type ShiftLike = { id: string };
type TimesheetLike = { jobWorker: { id: string }; shift?: ShiftLike | null };

/**
 * The worker's timesheet for the focused occurrence.
 *
 * Falls back to their first timesheet when the focused shift has none for them
 * — hired to the job but did not work that day — so the row still renders its
 * identity and actions instead of blanking.
 */
export const timesheetForShift = <T extends TimesheetLike>(
  timesheets: readonly T[],
  jobWorkerId: string,
  focusedShiftId?: string | null,
): T | undefined => {
  const mine = timesheets.filter((ts) => ts.jobWorker.id === jobWorkerId);
  if (!focusedShiftId) return mine[0];

  return (
    mine.find((ts) => String(ts.shift?.id) === String(focusedShiftId)) ??
    mine[0]
  );
};

/**
 * The shift the page is anchored to. Edit dialogs parse "HH:mm" against this
 * shift's DATE, so a mismatch writes the right time on the wrong day.
 *
 * An id that belongs to another job (or a typo in the URL) resolves to the
 * first shift rather than `undefined` — the page must degrade to its historical
 * behaviour, never render a broken editor.
 */
export const shiftForId = <T extends ShiftLike>(
  shifts: readonly T[],
  focusedShiftId?: string | null,
): T | undefined => {
  if (!focusedShiftId) return shifts[0];
  return (
    shifts.find((sh) => String(sh.id) === String(focusedShiftId)) ?? shifts[0]
  );
};

export const getShiftTime = (timesheet) => {
  const {
    netApprovedTime,
    modifiedCheckinAt,
    modifiedCheckoutAt,
    modifiedBreakMinutes,
    reportedCheckinAt,
    reportedCheckoutAt,
    reportedBreakMinutes,
    checkinAt,
    checkoutAt,
  } = timesheet;
  let approvedTime = netApprovedTime;
  if (!approvedTime && modifiedCheckinAt && modifiedCheckoutAt) {
    approvedTime = calculateNetTime(
      modifiedCheckinAt,
      modifiedCheckoutAt,
      modifiedBreakMinutes,
    );
  } else if (!approvedTime && reportedCheckinAt && reportedCheckoutAt) {
    approvedTime = calculateNetTime(
      reportedCheckinAt,
      reportedCheckoutAt,
      reportedBreakMinutes,
    );
  } else if (!approvedTime && checkinAt && checkoutAt) {
    approvedTime = calculateNetTime(
      checkinAt,
      checkoutAt,
      reportedBreakMinutes,
    );
  }
  return approvedTime / 60;
};

export const showNeedsApprovalAndReviewChip = (
  workerInfo: any,
  timeSheet?: any,
  job?: GetJobQuery['job'],
) => {
  const tsStatus = getTimeSheetStatus(timeSheet);
  const showDashboardApprovalStatus = useFeatureValue(
    FEATURE_TOGGLE.DashboardApprovalStatusFeature,
    false,
  );
  if (!showDashboardApprovalStatus) {
    return tsStatus === TimeSheetStatus.PENDING;
  }

  const jobWorker = job?.jobWorkers.find((x) => x.worker.id === workerInfo.id);
  const isHired =
    jobWorker &&
    jobWorker.dismissedAt === null &&
    jobWorker.droppedAt === null &&
    jobWorker.noShowMarkedAt === null;
  return (
    tsStatus === TimeSheetStatus.PENDING ||
    (tsStatus === TimeSheetStatus.INCOMPLETE &&
      job?.status === JobStatusEnum.COMPLETED) ||
    (job?.status === JobStatusEnum.COMPLETED && isHired && !tsStatus)
  );
};

export const RenderWorkerInfo = ({
  workerInfo,
  isClientAdmin,
  StatusChip,
  job,
  timeSheet,
  relationshipStatus,
  createWorkerRelationship,
  deleteWorkerRelationship,
  isMissingTimesheet,
  isMissingCheckout,
}: {
  workerInfo: any;
  isClientAdmin: boolean;
  StatusChip?: ReactNode;
  handleAddComment?: any;
  timeSheet?: any;
  job?: GetJobQuery['job'];
  relationshipStatus: RelationshipKindEnum | null;
  createWorkerRelationship: (
    reason: string,
    comments: string,
    kind: RelationshipKindEnum,
    triggeredFrom: string,
  ) => void;
  deleteWorkerRelationship: (
    kind: RelationshipKindEnum,
    triggeredFrom: string,
  ) => void;
  isMissingTimesheet?: boolean;
  isMissingCheckout?: boolean;
}) => {
  const [showProfileRevampModal, hideProfileRevampModal] = useModal(
    () => (
      <ProfileModalRevamp
        ProfileInfo={workerInfo}
        hideModal={hideProfileRevampModal}
        isClientAdmin={isClientAdmin}
        relationshipStatus={relationshipStatus}
        createWorkerRelationship={createWorkerRelationship}
        deleteWorkerRelationship={deleteWorkerRelationship}
      />
    ),
    [workerInfo, relationshipStatus],
  );
  const [highlight, setHighlight] = useState(false);
  const isCertified = containsGravyCertification(workerInfo.activeCertificates);

  const handleClick = () => {
    showProfileRevampModal();
  };
  const handleMouseEnter = () => setHighlight(true);
  const handleMouseLeave = () => setHighlight(false);

  const [anchorTooltip, setAnchorTooltip] = useState(null);
  const [anchorDismissedTooltip, setAnchorDismissedTooltip] = useState(null);
  const [anchorRemovedTooltip, setAnchorRemovedTooltip] = useState(null);
  const handlePopoverOpen = (event) => {
    setAnchorTooltip(event.currentTarget);
  };
  const handlePopoverClose = () => {
    setAnchorTooltip(null);
  };
  const handleDismissedPopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorDismissedTooltip(event.currentTarget);
  };
  const handleDismissedPopoverClose = () => {
    setAnchorDismissedTooltip(null);
  };
  const handleRemovedPopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorRemovedTooltip(event.currentTarget);
  };
  const handleRemovedPopoverClose = () => {
    setAnchorRemovedTooltip(null);
  };
  const openTooltip = Boolean(anchorTooltip);
  const openDismissedTooltip = Boolean(anchorDismissedTooltip);
  const openRemovedTooltip = Boolean(anchorRemovedTooltip);

  const { currentAdminIsCustomerAdmin } = useAuth();

  // Find the jobWorker to get dismiss information
  const jobWorker = job?.jobWorkers?.find((x) => x.worker.id === workerInfo.id);
  const isDismissed = jobWorker ? !!jobWorker.dismissType : false;
  const isRemoved = jobWorker
    ? jobWorker.status === JobWorkerStatusEnum.DISMISSED
    : false;

  const getDismissedTooltipContent = () => {
    if (!jobWorker || !jobWorker.dismissType) return null;

    const dismissedByName = jobWorker.dismissedBy
      ? `${jobWorker.dismissedBy.firstName || ''} ${jobWorker.dismissedBy.lastName || ''}`.trim()
      : '';

    let dismissedAtFormatted = '';
    if (jobWorker.dismissedAt) {
      try {
        const dismissedDate = parseISO(jobWorker.dismissedAt);
        dismissedAtFormatted = format(dismissedDate, 'h:mm a, MMM dd, yyyy');
      } catch (e) {
        dismissedAtFormatted = jobWorker.dismissedAt;
      }
    }

    return {
      dismissType: jobWorker.dismissType,
      dismissReason: jobWorker.dismissReason || '',
      dismissedByName,
      dismissedAtFormatted,
    };
  };

  const getRemovedTooltipContent = () => {
    if (!jobWorker || !isRemoved) return null;

    const removedByName = jobWorker.dismissedBy
      ? `${jobWorker.dismissedBy.firstName || ''} ${jobWorker.dismissedBy.lastName || ''}`.trim()
      : '';

    let removedAtFormatted = '';
    if (jobWorker.dismissedAt) {
      try {
        const removedDate = parseISO(jobWorker.dismissedAt);
        removedAtFormatted = format(removedDate, 'h:mm a, MMM dd, yyyy');
      } catch (e) {
        removedAtFormatted = jobWorker.dismissedAt;
      }
    }

    return {
      reason: jobWorker.dismissReason || '',
      removedByName,
      removedAtFormatted,
    };
  };

  const dismissedTooltipContent = getDismissedTooltipContent();
  const removedTooltipContent = getRemovedTooltipContent();

  const getNeedsApprovalOrReviewChip = () => {
    // Don't show needs approval/review chip if missing timesheet or missing checkout tags are present
    if (isMissingTimesheet || isMissingCheckout) return null;

    const shouldShowChip = showNeedsApprovalAndReviewChip(
      workerInfo,
      timeSheet,
      job,
    );

    if (!shouldShowChip) return null;

    const needsReview =
      !currentAdminIsCustomerAdmin && timeSheet && timeSheet.flagReason !== 0;

    if (needsReview) {
      return (
        <ApprovalAlert style={{ backgroundColor: colors.needsReview }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CustomTooltipJob
              anchorEl={anchorTooltip}
              handlePopoverClose={handlePopoverClose}
              open={openTooltip}
              needsReview={true}
              reason={timesheetFlagMessage(timeSheet, job.address)}
            />
            <InfoItalicIcon
              style={{ color: '#fff', height: '18px', width: '18px' }}
              onMouseEnter={handlePopoverOpen}
              onMouseLeave={handlePopoverClose}
            />
            <NoWrapSpan>Needs Review</NoWrapSpan>
          </Stack>
        </ApprovalAlert>
      );
    }

    return <ApprovalAlert>Needs Approval</ApprovalAlert>;
  };

  return (
    <MuiStack
      direction={'row'}
      spacing={1.5}
      sx={{ ':hover': { cursor: 'pointer' } }}
    >
      <MuiStack
        alignItems={'center'}
        id="worker-info-div"
        justifyContent={'center'}
        spacing={1.7}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Box sx={{ position: 'relative' }}>
          <Avatar
            border={isCertified}
            rounded={true}
            size="lg2"
            src={workerInfo.avatarUrl}
          />
          {isCertified && (
            <GravyTrained
              id="gravy-badge"
              sx={{
                position: 'absolute',
                right: 0,
                bottom: 0,
                width: '28px',
                height: '28px',
              }}
            />
          )}
        </Box>
      </MuiStack>
      <MuiStack spacing={1.2}>
        <div style={{ minHeight: '78px' }}>
          <WorkerDesc
            highlight={highlight}
            isClientAdmin={isClientAdmin}
            job={job}
            showRating={true}
            timesheet={timeSheet}
            workerInfo={workerInfo}
            relationshipStatus={relationshipStatus}
            createWorkerRelationship={createWorkerRelationship}
            deleteWorkerRelationship={deleteWorkerRelationship}
            onClick={handleClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          />
          <Stack alignItems={'center'} flexDirection={'row'} gap={'10px'}>
            <div className="worker-status-chip">
              {isRemoved && removedTooltipContent ? (
                <>
                  <RemovedTooltip
                    open={openRemovedTooltip}
                    anchorEl={anchorRemovedTooltip}
                    onClose={handleRemovedPopoverClose}
                    reason={removedTooltipContent.reason}
                    removedByName={removedTooltipContent.removedByName}
                    removedAtFormatted={
                      removedTooltipContent.removedAtFormatted
                    }
                  />
                  <StyledChip
                    icon={
                      <InfoItalicIcon
                        style={{
                          color: '#5C5C5C',
                          height: '16px',
                          width: '16px',
                        }}
                        onMouseEnter={handleRemovedPopoverOpen}
                        onMouseLeave={handleRemovedPopoverClose}
                      />
                    }
                    label="Removed"
                    size="small"
                    sx={{
                      fontSize: '0.8rem',
                      bgcolor: '#DDE1E0',
                      color: '#5C5C5C',
                      paddingLeft: '4px',
                      borderRadius: '8px',
                    }}
                  />
                </>
              ) : (
                StatusChip
              )}
            </div>
            {isMissingTimesheet && MissingTimesheetChip}
            {isMissingCheckout && MissingCheckoutChip}
            {isDismissed && dismissedTooltipContent && (
              <>
                <DismissedTooltip
                  open={openDismissedTooltip}
                  anchorEl={anchorDismissedTooltip}
                  onClose={handleDismissedPopoverClose}
                  dismissType={dismissedTooltipContent.dismissType}
                  dismissReason={dismissedTooltipContent.dismissReason}
                  dismissedByName={dismissedTooltipContent.dismissedByName}
                  dismissedAtFormatted={
                    dismissedTooltipContent.dismissedAtFormatted
                  }
                />
                <StyledChip
                  icon={
                    <InfoItalicIcon
                      style={{
                        color: '#1C1C1C',
                        height: '16px',
                        width: '16px',
                      }}
                      onMouseEnter={handleDismissedPopoverOpen}
                      onMouseLeave={handleDismissedPopoverClose}
                    />
                  }
                  label="Dismissed"
                  size="small"
                  sx={{
                    borderRadius: '8px',
                    background: '#F4F4F4',
                    display: 'flex',
                    padding: '0 8px',
                    alignItems: 'center',
                    gap: '4px',
                    color: '#1C1C1C',
                    fontFamily: 'Manrope',
                    fontSize: '11px',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: '15.4px',
                    textTransform: 'uppercase',
                  }}
                />
              </>
            )}
            {getNeedsApprovalOrReviewChip()}
          </Stack>
        </div>
      </MuiStack>
    </MuiStack>
  );
};

export const WorkerPayRate = ({
  job,
  workerId,
  timesheet,
  clientAdmin,
  addSeperator,
}: WorkerPayRateProps) => {
  const tipsInCents = useFeatureValue(FEATURE_TOGGLE.TipsInCents, false);

  const jobWorker = job?.jobWorkers?.find((x) => x.worker.id === workerId);
  let amount: string = '';
  if (clientAdmin) {
    if (timesheet && timesheet.tipAmount) {
      if (tipsInCents) {
        const tip = centsToCurrency(Math.round(timesheet.tipAmount * 100));
        amount = `${tip} Tip`;
      } else {
        amount = `${timesheet.tipAmount} Tip`;
      }
    }
  } else if (jobWorker) {
    amount = centsToCurrency(jobWorker.payRate) + '/ hr';
  }

  if (amount.length) {
    return (
      <>
        {addSeperator && (
          <div style={{ marginLeft: 15, marginRight: 10 }}>|</div>
        )}
        <Stack alignItems={'center'} flexDirection={'row'} gap={'2px'}>
          <AccountDollarIcon
            sx={{
              width: '16px',
              height: '16px',
            }}
          />
          <CardCriteria>{amount}</CardCriteria>
        </Stack>
      </>
    );
  }
  return null;
};

export const WorkerDesc = ({
  workerInfo,
  isClientAdmin,
  showRating,
  highlight = false,
  job,
  timesheet,
  relationshipStatus,
  createWorkerRelationship,
  deleteWorkerRelationship,
  onClick,
  onMouseEnter,
  onMouseLeave,
}: {
  workerInfo: any;
  isClientAdmin: boolean;
  showRating?: boolean;
  highlight?: boolean;
  job?: GetJobQuery['job'];
  timesheet?: TimesheetItem;
  relationshipStatus: RelationshipKindEnum | null;
  createWorkerRelationship: (
    reason: string,
    comments: string,
    kind: RelationshipKindEnum,
    triggeredFrom: string,
  ) => void;
  deleteWorkerRelationship: (
    kind: RelationshipKindEnum,
    triggeredFrom: string,
  ) => void;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}) => {
  const { firstName, middleName, lastName } = workerInfo.user;
  return (
    <div onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <Stack
        flexDirection={'row'}
        style={{ alignItems: 'flex-start' }}
        gap={'5px'}
      >
        <Body
          color={highlight ? 'themeDefault' : undefined}
          css={{
            lineHeight: '1 !important',
            textDecoration: highlight ? 'underline' : 'none',
            display: 'flex',
            flexWrap: 'wrap',
            paddingTop: '7px',
          }}
          weight="medium"
          onClick={onClick}
        >
          {firstName} {middleName} {lastName}
        </Body>
        {relationshipStatus === RelationshipKindEnum.BLOCKED ? (
          <BlockIconWithBackground
            style={{
              marginLeft: '3px',
            }}
          />
        ) : (
          <PermissionComponent id="toggle-favorite-btn" showAsReadOnly>
            <IconicButton
              a11yLabel="Toggle Favorite"
              appearance="clear"
              transparentOnHover={true}
              customIcon={
                relationshipStatus === RelationshipKindEnum.FAVORITED ? (
                  <HeartFillIcon
                    style={{
                      cursor: 'pointer',
                    }}
                  />
                ) : (
                  <HeartIcon
                    style={{
                      cursor: 'pointer',
                    }}
                  />
                )
              }
              data-testid="toggle-favorite"
              icon={faCircle}
              id="toggle-favorite-btn"
              size="sm"
              type="button"
              onClick={() => {
                if (relationshipStatus === RelationshipKindEnum.FAVORITED) {
                  deleteWorkerRelationship(
                    RelationshipKindEnum.FAVORITED,
                    'Worker_Modal',
                  );
                } else {
                  createWorkerRelationship(
                    '',
                    '',
                    RelationshipKindEnum.FAVORITED,
                    'Worker_Modal',
                  );
                }
              }}
            />
          </PermissionComponent>
        )}
      </Stack>
      {showRating && (
        <MuiStack
          alignItems={'center'}
          direction={'row'}
          marginY={1}
          spacing={0.6}
          onClick={onClick}
        >
          <StarIcon
            id="star-icon"
            sx={{
              color:
                workerInfo.averageRating != null &&
                Number(workerInfo.averageRating) > 0
                  ? '#faaf00'
                  : '#D1D5DB',
              width: '20px',
              height: '20px',
            }}
          />
          {workerInfo.averageRating != null &&
          Number(workerInfo.averageRating) > 0 ? (
            <>
              <Text size={'md'} weight={'semibold'}>
                {Number(workerInfo.averageRating).toFixed(1)}
              </Text>
              {workerInfo.jobsRatingCount != null &&
                Number(workerInfo.jobsRatingCount) > 0 && (
                  <Text
                    color={'lighter'}
                  >{`(${workerInfo.jobsRatingCount})`}</Text>
                )}
            </>
          ) : (
            <div style={{ marginLeft: '-3px' }}>
              <Text size={'md'} weight={'semibold'}>
                <span style={{ color: '#D1D5DB' }}>-</span>
              </Text>
            </div>
          )}
          <WorkerPayRate
            addSeperator={true}
            clientAdmin={isClientAdmin}
            job={job}
            timesheet={timesheet}
            workerId={workerInfo.id}
          />
        </MuiStack>
      )}
    </div>
  );
};

export const renderTimeSheetStatusChip = (status?: TimeSheetStatus) => {
  if (!status) return '-';
  switch (status) {
    case TimeSheetStatus.INCOMPLETE:
      return (
        <Text color={'light'} size={'sm'}>
          {'Incomplete'}
        </Text>
      );
    case TimeSheetStatus.PENDING:
      return <StyledChip label={'Pending'} size="small" />;
    case TimeSheetStatus.APPROVED:
      return <StyledChip color="success" label={'Approved'} size="small" />;
    case TimeSheetStatus.REJECTED:
      return <StyledChip color="error" label={'Rejected'} size="small" />;
    default:
      break;
  }
};

export const MenuItemsObject = {
  addComment: (handleAddComment, showComment) =>
    showComment ? (
      <MenuItem id="btn-add-comment" onClick={handleAddComment}>
        <ChatOutlined htmlColor={IconColor} style={{ marginRight: '10px' }} />
        Add Comment
      </MenuItem>
    ) : undefined,
  noShow: (handleToggleShowed) => (
    <MenuItem id="btn-noshow" onClick={handleToggleShowed}>
      <CancelNoShowIcon style={{ marginRight: '10px' }} />
      Mark as No Show
    </MenuItem>
  ),
  cancelNoshow: (handleToggleShowed) => (
    <MenuItem id="btn-cancel-noshow" onClick={handleToggleShowed}>
      <CancelNoShowIcon style={{ marginRight: '10px' }} />
      Cancel No show
    </MenuItem>
  ),
  removeWorker: (handleRemoveTimesheet) => (
    <MenuItem id="btn-remove" onClick={handleRemoveTimesheet}>
      <RemoveIcon htmlColor={IconColor} style={{ marginRight: '10px' }} />
      Remove
    </MenuItem>
  ),
  showMap: (handleShowMap) => (
    <MenuItem id="btn-showmap" onClick={handleShowMap}>
      <MapMarkerIcon htmlColor={IconColor} style={{ marginRight: '10px' }} />
      View Map
    </MenuItem>
  ),
  addToPool: (handleAddToPool) => (
    <MenuItem id="btn-add-to-pool" onClick={handleAddToPool}>
      <HeartIcon fontSize="small" style={{ marginRight: '10px' }} />
      Add worker to pool
    </MenuItem>
  ),
  addTip: (handleAddTip) => (
    <MenuItem id="btn-addTip" onClick={handleAddTip}>
      <AddTipIcon htmlColor={IconColor} style={{ marginRight: '10px' }} />
      Tip Worker
    </MenuItem>
  ),
  enable: (enableActionCallback) => (
    <MenuItem id="btn-enable" onClick={enableActionCallback}>
      <ClientApproveIcon
        style={{
          marginLeft: 4,
          fontSize: '18px',
          marginRight: 10,
        }}
      />
      Enable
    </MenuItem>
  ),
  disable: (disableActionCallback) => (
    <MenuItem id="btn-disable" onClick={disableActionCallback}>
      <NoshowIcon
        style={{
          marginLeft: 4,
          fontSize: '17px',
          marginRight: 10,
        }}
      />
      Disable
    </MenuItem>
  ),
  approve: (handleApproveTimesheet) => (
    <MenuItem id="btn-approve" onClick={handleApproveTimesheet}>
      <CheckCircleOutline
        fontSize="small"
        htmlColor={IconColor}
        style={{ marginRight: '10px' }}
      />
      Approve
    </MenuItem>
  ),
  reject: (handleRejectTimesheet) => (
    <MenuItem id="btn-reject" onClick={handleRejectTimesheet}>
      <CancelOutlined
        fontSize="small"
        htmlColor={IconColor}
        style={{ marginRight: '10px' }}
      />
      Reject
    </MenuItem>
  ),
  unReject: (handleUnrejectTimesheet) => (
    <MenuItem id="btn-unreject" onClick={handleUnrejectTimesheet}>
      <ClientUnRejectIcon
        fontSize="small"
        htmlColor={IconColor}
        style={{ marginRight: '10px' }}
      />
      Unreject
    </MenuItem>
  ),
  viewTimesheetLogs: (handleViewTimesheetLogs) => (
    <MenuItem id="btn-view-timesheet-logs" onClick={handleViewTimesheetLogs}>
      <InfoItalicIcon htmlColor={IconColor} style={{ marginRight: '10px' }} />
      View Timesheet Logs
    </MenuItem>
  ),
  dismissWorker: (handleDismissWorker) => (
    <MenuItem id="btn-dismiss-worker" onClick={handleDismissWorker}>
      <RemoveIcon htmlColor={IconColor} style={{ marginRight: '10px' }} />
      Dismiss Worker
    </MenuItem>
  ),
  undismissWorker: (handleUndismissWorker) => (
    <MenuItem id="btn-undismiss-worker" onClick={handleUndismissWorker}>
      <CheckCircleOutline
        fontSize="small"
        htmlColor={IconColor}
        style={{ marginRight: '10px' }}
      />
      Undismiss Worker
    </MenuItem>
  ),
  removedFromAssignment: (handleRemovedFromAssignment) => (
    <MenuItem
      id="btn-removed-from-assignment"
      onClick={handleRemovedFromAssignment}
    >
      <RemoveIcon htmlColor={IconColor} style={{ marginRight: '10px' }} />
      Removed from Assignment
    </MenuItem>
  ),
};

export const RemovedWorkerChip = (
  <StyledChip
    icon={
      <InfoItalicIcon
        style={{
          color: '#5C5C5C',
        }}
      />
    }
    label="Removed"
    size="small"
    sx={{
      fontSize: '0.8rem',
      bgcolor: '#DDE1E0',
      color: '#5C5C5C',
      paddingLeft: '4px',
      borderRadius: '8px',
    }}
  />
);

export const NoShowChip = (
  <StyledChip
    icon={
      <InfoItalicIcon
        style={{
          color: '#DC1515',
        }}
      />
    }
    label="No Show"
    size="small"
    sx={{
      fontSize: '0.8rem',
      color: '#DC1515',
      bgcolor: '#FFE1E1',
      paddingLeft: '4px',
      borderRadius: '8px',
    }}
  />
);
export const HiredChip = (
  <StyledChip
    label="Hired"
    size="small"
    sx={{
      fontSize: '0.8rem',
      bgcolor: '#E0EBFF',
      color: '#3A5FA0',
      borderRadius: '8px',
    }}
  />
);

export const ConfirmedChip = (
  <StyledChip
    label="Confirmed"
    size="small"
    sx={{
      fontSize: '0.75rem',
      bgcolor: '#45a735',
      color: '#FFF',
    }}
  />
);

export const MissingTimesheetChip = (
  <StyledChip
    label="MISSING TIMESHEET"
    size="small"
    sx={{
      borderRadius: '8px',
      background: '#C10000',
      display: 'flex',
      padding: 0,
      alignItems: 'center',
      gap: 0,
      color: '#FFF4F3',
      fontFamily: 'Manrope',
      fontSize: '11px',
      fontStyle: 'normal',
      fontWeight: 700,
      lineHeight: '15.4px',
      textTransform: 'uppercase',
    }}
  />
);

export const MissingCheckoutChip = (
  <StyledChip
    label="MISSING CHECK-OUT"
    size="small"
    sx={{
      borderRadius: '8px',
      background: '#C10000',
      display: 'flex',
      padding: 0,
      alignItems: 'center',
      gap: 0,
      color: '#FFF4F3',
      fontFamily: 'Manrope',
      fontSize: '11px',
      fontStyle: 'normal',
      fontWeight: 700,
      lineHeight: '15.4px',
      textTransform: 'uppercase',
    }}
  />
);

export const TimesheetStatusChip = ({ status, needsReview }) => {
  let bgColor = '#000';
  let label = 'Approved';
  if (status === TimesheetStatusEnum.REJECTED) {
    label = 'Rejected';
    bgColor = '#DC1515';
  } else if (status === TimesheetStatusEnum.PENDING) {
    if (needsReview) {
      label = 'Needs Review';
    } else {
      label = 'Needs Approval';
    }

    bgColor = '#45a735';
  } else if (status !== TimesheetStatusEnum.APPROVED) {
    return <></>;
  }

  return (
    <StyledChip
      label={label}
      size="small"
      sx={{
        fontSize: '0.75rem',
        bgcolor: bgColor,
        color: '#FFF',
      }}
    />
  );
};

export const InvitedChip = (
  <StyledChip
    label="Invited"
    size="small"
    sx={{
      fontSize: '0.8rem',
      borderRadius: '8px',
      color: '#106F00',
      bgcolor: '#DDEFD6',
    }}
  />
);

export const DroppedChip = (
  <StyledChip
    icon={
      <InfoItalicIcon
        style={{
          color: '#ECA300',
        }}
      />
    }
    label="Dropped"
    size="small"
    sx={{
      fontSize: '0.8rem',
      bgcolor: '#FFF0C8',
      color: '#ECA300',
      paddingLeft: '4px',
      borderRadius: '8px',
    }}
  />
);

export const LateDropChip = (
  <StyledChip
    icon={
      <InfoItalicIcon
        style={{
          color: '#ECA300',
        }}
      />
    }
    label="Late Drop"
    size="small"
    sx={{
      fontSize: '0.8rem',
      bgcolor: '#FFF0C8',
      color: '#ECA300',
      paddingLeft: '4px',
      borderRadius: '8px',
    }}
  />
);

export const DeclinedChip = (
  <StyledChip
    icon={
      <InfoItalicIcon
        style={{
          color: '#7456ED',
        }}
      />
    }
    label="Declined"
    size="small"
    sx={{
      fontSize: '0.8rem',
      color: '#7456ED',
      bgcolor: '#F4ECFF',
      paddingLeft: '4px',
      borderRadius: '8px',
    }}
  />
);

// GRAVY-9730: Helper to determine the correct chip and messaging for a dropped worker
export const getDropDisplayInfo = (
  removalType: string | null | undefined,
  isLateDrop: boolean | null | undefined,
  suspensionType: string | null | undefined,
  isClientAdmin: boolean,
): {
  chip: ReactNode;
  workerStatus: 'Dropped' | 'Removed';
  message: string | null;
} => {
  // Platform-initiated removal → "Removed" chip (gray)
  if (removalType && removalType !== 'worker_initiated') {
    let message = 'Removed';
    if (removalType === 'suspension') {
      message =
        suspensionType === 'permanent'
          ? 'Removed – Account Permanently Suspended'
          : 'Removed – Account Temporarily Suspended';
    } else if (removalType === 'account_disabled') {
      message = 'Removed – Account Disabled';
    } else if (removalType === 'policy_enforcement') {
      message = 'Removed – Policy Enforcement';
    } else if (removalType === 'safety_compliance') {
      message = 'Removed – Safety & Compliance';
    }
    return { chip: RemovedWorkerChip, workerStatus: 'Removed', message };
  }

  // Worker-initiated late drop, client view → sanitized messaging
  if (isLateDrop && isClientAdmin) {
    return {
      chip: LateDropChip,
      workerStatus: 'Dropped',
      message: 'Late Drop – Policy Violation Under Review',
    };
  }

  // Worker-initiated late drop, admin view → show as Late Drop with original reason
  if (isLateDrop) {
    return {
      chip: LateDropChip,
      workerStatus: 'Dropped',
      message: null,
    };
  }

  // Regular worker drop
  return { chip: DroppedChip, workerStatus: 'Dropped', message: null };
};

// Removed Tooltip Component
const RemovedTooltip = ({
  open,
  anchorEl,
  onClose,
  reason,
  removedByName,
  removedAtFormatted,
}: {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  reason?: string;
  removedByName?: string;
  removedAtFormatted?: string;
}) => {
  if (!anchorEl) return null;

  return (
    <Popover
      disableRestoreFocus
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      open={open}
      slotProps={{
        paper: {
          style: {
            maxWidth: '269px',
            width: '269px',
            boxShadow: '0px 0px 12px rgba(0, 0, 0, 0.12)',
            border: '1px solid #E2E2E2',
          },
          className: 'afterArrow',
        },
      }}
      sx={{
        pointerEvents: 'none',
        '& .MuiPopover-paper': {
          marginBottom: '16px',
          transform: 'translateY(-100%)',
        },
      }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      onClose={onClose}
    >
      <div style={{ padding: '12px 0px 20px 0px' }}>
        {reason && (
          <div style={{ display: 'block', padding: '0px 12px 0px 16px' }}>
            <div
              style={{
                color: 'black',
                lineHeight: 'normal',
                letterSpacing: '.17px',
                padding: '0px 0px',
              }}
            >
              <Text
                css={{
                  color: 'black',
                  lineHeight: 'normal',
                  letterSpacing: '.17px',
                  fontSize: 12,
                  fontWeight: 400,
                  width: '150px',
                }}
                wordWrap="breakWord"
              >
                {reason}
              </Text>
            </div>
          </div>
        )}
        {removedByName && removedAtFormatted && (
          <div style={{ display: 'block', padding: '0px 12px 0px 16px' }}>
            <div
              style={{
                color: 'black',
                lineHeight: 'normal',
                letterSpacing: '.17px',
                padding: '0px 0px',
              }}
            >
              <Text
                css={{
                  color: 'black',
                  lineHeight: 'normal',
                  letterSpacing: '.17px',
                  fontSize: 12,
                  fontWeight: 400,
                  width: '150px',
                }}
                wordWrap="breakWord"
              >
                by {removedByName}, {removedAtFormatted}
              </Text>
            </div>
          </div>
        )}
      </div>
    </Popover>
  );
};

// Dismissed Tooltip Component
const DismissedTooltip = ({
  open,
  anchorEl,
  onClose,
  dismissType,
  dismissReason,
  dismissedByName,
  dismissedAtFormatted,
}: {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  dismissType: string;
  dismissReason?: string;
  dismissedByName?: string;
  dismissedAtFormatted?: string;
}) => {
  if (!anchorEl) return null;

  return (
    <Popover
      disableRestoreFocus
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      open={open}
      slotProps={{
        paper: {
          style: {
            maxWidth: '269px',
            width: '269px',
            boxShadow: '0px 0px 12px rgba(0, 0, 0, 0.12)',
            border: '1px solid #E2E2E2',
          },
          className: 'afterArrow',
        },
      }}
      sx={{
        pointerEvents: 'none',
        '& .MuiPopover-paper': {
          marginBottom: '16px',
          transform: 'translateY(-100%)',
        },
      }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      onClose={onClose}
    >
      <div style={{ padding: '12px 0px 20px 0px' }}>
        <div style={{ display: 'block', padding: '0px 12px 0px 16px' }}>
          <div
            style={{
              color: 'black',
              lineHeight: 'normal',
              letterSpacing: '.17px',
              padding: '0px 0px',
            }}
          >
            <Text
              css={{
                color: 'black',
                lineHeight: 'normal',
                letterSpacing: '.17px',
                padding: '0px 4px 0px 0px',
                fontSize: 12,
                fontWeight: 700,
              }}
              wordWrap="breakWord"
            >
              {dismissType}
            </Text>
          </div>
        </div>
        {dismissReason && (
          <div style={{ display: 'block', padding: '0px 12px 0px 16px' }}>
            <div
              style={{
                color: 'black',
                lineHeight: 'normal',
                letterSpacing: '.17px',
                padding: '0px 0px',
              }}
            >
              <Text
                css={{
                  color: 'black',
                  lineHeight: 'normal',
                  letterSpacing: '.17px',
                  fontSize: 12,
                  fontWeight: 400,
                  width: '150px',
                }}
                wordWrap="breakWord"
              >
                {dismissReason}
              </Text>
            </div>
          </div>
        )}
        {dismissedByName && dismissedAtFormatted && (
          <div style={{ display: 'block', padding: '0px 12px 0px 16px' }}>
            <div
              style={{
                color: 'black',
                lineHeight: 'normal',
                letterSpacing: '.17px',
                padding: '0px 0px',
              }}
            >
              <Text
                css={{
                  color: 'black',
                  lineHeight: 'normal',
                  letterSpacing: '.17px',
                  fontSize: 12,
                  fontWeight: 400,
                  width: '150px',
                }}
                wordWrap="breakWord"
              >
                by {dismissedByName}, {dismissedAtFormatted}
              </Text>
            </div>
          </div>
        )}
      </div>
    </Popover>
  );
};
